import type { MarketDataSnapshot, Security } from '@talos/kyoko';
import {
  DepthTypeEnum,
  LiquidityTypeEnum,
  MARKET_DATA_SNAPSHOT,
  useObservableValue,
  useSecuritiesContext,
  useSparklinesData,
  useSubscription,
  useWLOrderFormSelector,
  wsScanToMap,
} from '@talos/kyoko';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { MarketDataContext } from './MarketDataContext';

export function MarketDataProvider({ children }: PropsWithChildren) {
  const { securitiesList, securitiesBySymbol } = useSecuritiesContext();
  const favoredPairCurrency = useWLOrderFormSelector(state => state.swap.referenceData.favoredPairCurrency);
  const homeCurrency = useWLOrderFormSelector(state => state.swap.referenceData.homeCurrency);

  const favoredTradablePairs = useMemo(() => {
    const filtered = securitiesList.filter(sec => sec.QuoteCurrency === favoredPairCurrency?.Symbol);

    // Try to find the favoredPairCurrency-homeCurrency pair (e.g., USDT-PHP if favoredPairCurrency=USDT, homeCurrency=PHP)
    let extraPair: Security | undefined = undefined;
    if (favoredPairCurrency?.Symbol && homeCurrency?.Symbol) {
      extraPair = securitiesList.find(
        sec => sec.BaseCurrency === favoredPairCurrency.Symbol && sec.QuoteCurrency === homeCurrency.Symbol
      );
    }

    // Add the extra pair if it exists and isn't already included
    return extraPair && !filtered.some(sec => sec.Symbol === extraPair!.Symbol) ? [...filtered, extraPair] : filtered;
  }, [securitiesList, favoredPairCurrency?.Symbol, homeCurrency?.Symbol]);

  const symbols = useMemo(() => favoredTradablePairs.map(s => s.Symbol), [favoredTradablePairs]);
  const sparklinesObs = useSparklinesData(symbols);
  const sparklineBySymbol = useObservableValue(() => sparklinesObs, [sparklinesObs]);

  const snapshotRequest = useMemo(() => {
    return favoredTradablePairs.flatMap(security => [
      {
        name: MARKET_DATA_SNAPSHOT,
        tag: `market-data-provider-gcash`,
        DepthType: DepthTypeEnum.Price,
        Symbol: security.Symbol,
        LiquidityType: LiquidityTypeEnum.Indicative,
        UnifiedLiquidity: 'Disabled',
        Depth: 1,
        Throttle: '1s',
      },
    ]);
  }, [favoredTradablePairs]);

  const { data: snapshotsSubscription } = useSubscription<MarketDataSnapshot>(snapshotRequest);

  const marketDataByCurrency = useObservableValue(
    () =>
      snapshotsSubscription.pipe(
        wsScanToMap({
          getUniqueKey: snapshot =>
            snapshot.Symbol.replaceAll(`-${favoredPairCurrency?.Symbol}`, '').replaceAll(
              `-${homeCurrency?.Symbol}`,
              ''
            ),
          getInsertable: snapshot => {
            return { marketData: snapshot, security: securitiesBySymbol.get(snapshot.Symbol)! };
          },
          newMapEachUpdate: true,
        })
      ),
    [snapshotsSubscription, favoredPairCurrency?.Symbol, homeCurrency?.Symbol, securitiesBySymbol],
    new Map()
  );

  const value = useMemo(() => {
    return { marketDataByCurrency, sparklineBySymbol };
  }, [marketDataByCurrency, sparklineBySymbol]);

  return <MarketDataContext.Provider value={value}>{children}</MarketDataContext.Provider>;
}
